import { useEffect, useMemo, useReducer } from "react";
import { useSelector } from 'react-redux';
import { IReduxAppState } from "../../../reducers";
import { IOrders } from "../../../interfaces/IOrder";
import Styles from "../../../scss/orderHistory.module.scss";
import IColumn from "../../../yoello-lib/modules/components/lists/interfaces/IColumn";
import { OrderListHeaders } from "./OrderListViewHeaders";
import { OrderListView } from "./OrderListView";
import { HTTPMethods } from "../../../yoello-lib/modules/API/API";
import { IMessageOptions } from "../../../yoello-lib/modules/API/useAPI";
import { orderFilter, orderFilterCount } from "../../../utils/filter";
import { IOrderListState, OrderListReducer } from "./state/OrderList.reducer";
import { DialogOrderTypes, FilterItemsInitialState, IProcessedOrders } from "../constants";
import { OrderListActionBar } from "./OrderListActionBar";
import useDebounce from "../../../hooks/useDebounce";
import { setFilterCount, setFilterState } from "./state/OrderList.action";
import { useTranslation } from "react-i18next";
import { IFilters } from "../../../interfaces/IOrderFilters";
import { ICallAPIFunction } from "../../../yoello-lib/modules/API2/types";
import { useHistory } from "react-router";
import { dialogKeys } from "../../../const/URL.const";
import { useDialogRouteStages } from "../../dialogRoutes/useDialogRouteStages";
import { generateTemplateURL } from "../../../services/generateConsumerURLs";
import { ListItemsLoading } from "../../customersDetails/ui/Loading";

export type orderAPICall = (method: HTTPMethods, messageOptions?: IMessageOptions) => Promise<IOrders[] | IOrders | undefined>;

export interface IEmptyMessages {
    title?: string;
    info?: string;
}
const searchTermMessage = { "info": "order.emptyList.amendSearch" }
interface IOrderListProps {
    mainKey: string;
    columns: IColumn<string>[];
    isLoading: boolean;
    orderAPICall: orderAPICall;
    orderList: IProcessedOrders[];
    venueId: string;
    cookie?: string;
    canFinishAll?: boolean;
    canSearchItems?: boolean;
    /**Function for refreshing the context search */
    refreshOrderList?: () => void;
    emptyMessageObject?: IEmptyMessages;
    callPlacedOrders?: ICallAPIFunction<any>;
    path: string;
}

export function OrderList({
    mainKey: key,
    columns,
    isLoading,
    orderAPICall,
    orderList,
    venueId,
    cookie,
    canFinishAll,
    canSearchItems,
    refreshOrderList,
    emptyMessageObject,
    path
}: IOrderListProps) {
    const { t } = useTranslation();

    /**We add the cookie to inital state if it exists */
    const initalState: IOrderListState = useMemo(() => {
        let filterState: any = {};
        if (cookie) {
            const cookieDetails = localStorage.getItem(cookie);
            if (cookieDetails) {
                filterState = JSON.parse(cookieDetails);
            }
        }
        return {
            searchTerm: "",
            filterState: { ...FilterItemsInitialState, tableAreas: {}, ...filterState },
            filterCount: 0,
            searchState: { key: "order_hash_id", value: t("orderHistory.search.orderId") }
        };
        //eslint-disable-next-line
    }, []);
    const selectedVenueId = useSelector((state: IReduxAppState) => state.selectedVenueId);
    const [state, dispatch] = useReducer(OrderListReducer, initalState);
    const { searchTerm, filterState, filterCount, searchState }: IOrderListState = state;
    const { debouncedValue: debouncedSearchTerm } = useDebounce(searchTerm, 500, true);
    const history = useHistory();
    const { goToNextStep } = useDialogRouteStages(history);

    useEffect(() => {
        if (debouncedSearchTerm !== undefined && !cookie) {
            const obj = {
                HOME_DELIVERY: filterState.HOME_DELIVERY,
                COLLECTION: filterState.COLLECTION,
                TABLE_DELIVERY: filterState.TABLE_DELIVERY
            };
            applyFilters(obj, filterState.tableAreas, filterCount, false);
        }
        // eslint-disable-next-line
    }, [debouncedSearchTerm]);
    useEffect(() => {
        if (cookie) {
            const cookieDetails = localStorage.getItem(cookie);
            if (cookieDetails) {
                const filters = JSON.parse(cookieDetails);
                dispatch(setFilterState(filters));
                const count = orderFilterCount(filters);
                dispatch(setFilterCount(count));
                // window.document.cookie = `${cookie}=${JSON.stringify(filters)};path=/;`;
            }
        } else {
            dispatch(setFilterState(initalState.filterState));
            dispatch(setFilterCount(0));
        }
        // eslint-disable-next-line    
    }, [selectedVenueId, cookie])


    useEffect(() => {
        if (cookie) {
            localStorage.setItem(cookie, JSON.stringify(filterState));
            // window.document.cookie = `${cookie}=${JSON.stringify(filterState)};path=/;`;
            if (refreshOrderList) {
                refreshOrderList();
            }
        }
        // eslint-disable-next-line        
    }, [filterState]);

    function applyFilters(orderTypes: Record<string, boolean>, areas: Record<string, boolean>, filtersCount: number, filtersChanged: boolean = false) {
        const query: string[] = [];
        const filter = {
            ...orderTypes,
            tableAreas: areas
        } as IFilters;
        if (filtersChanged) {
            dispatch(setFilterState(filter));
            dispatch(setFilterCount(filtersCount));
        }
        if (!refreshOrderList) {
            orderFilter(filter, query);
            if (debouncedSearchTerm.trim().length > 0) {
                query.push(`search_column=${searchState.key}`);
                const encode = encodeURIComponent(debouncedSearchTerm.trim());
                query.push(`search_text=${encode}`);
            }
            if (query.length > 0) {
                const queryString = `?${query.join("&")}`;
                orderAPICall(HTTPMethods.GET, { query: queryString });
            } else {
                orderAPICall(HTTPMethods.GET);
            }
        } else {
            refreshOrderList();
        }
    }
    function openOrderDetails(item: IProcessedOrders) {
        goToNextStep(
            dialogKeys.ORDER,
            {
                ComponentProps: { orderID: item.id , orderAPICall:orderAPICall , url:generateTemplateURL(`${item.id}`),  dialogType: DialogOrderTypes.ORDER_HISTORY, }
            }, `${path}/${item.id}`, null)
    }
    return <div key={`orderlist-${key}`} className={Styles.orderHistoryContainer}>
        <OrderListActionBar mainKey={key} selectedFilterCount={filterCount} filterState={filterState} applyFilters={applyFilters}
            dispatch={dispatch} selectedSearchState={searchState} isLoading={isLoading} refreshOrderList={refreshOrderList}
            venueId={venueId} canFinishAll={canFinishAll} orderList={orderList} canSearchItems={canSearchItems}
        />
        <div key={`columnHeaders-${key}`} className={Styles.columnsHeader}>
            {columns.map((column) => <OrderListHeaders {...column} key={`col-${key}-${column.key}-${column.label}`} />)}
        </div>
        {isLoading ? <ListItemsLoading /> :
            <OrderListView orderList={orderList} columns={columns} emptyMessageObject={searchTerm && searchTerm.length > 0 ? searchTermMessage : emptyMessageObject}
                openOrderDialog={openOrderDetails} />
        }
    </div>;
}




